import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class CacheTagService {
  private readonly keyPrefix = 'wtg-cache-tags:key:';
  private readonly tagPrefix = 'wtg-cache-tags:tag:';

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  indexKey(cacheKey: string, tags: string[]): void {
    if (!this.isBrowser()) {
      return;
    }
    const normalized = this.normalizeTags(tags);
    this.removeKey(cacheKey);
    if (!normalized.length) {
      return;
    }
    localStorage.setItem(this.keyPrefix + cacheKey, JSON.stringify(normalized));
    normalized.forEach((tag) => {
      const keys = new Set(this.getKeysForTag(tag));
      keys.add(cacheKey);
      this.setKeysForTag(tag, Array.from(keys));
    });
  }

  invalidateTags(tags: string[]): string[] {
    if (!this.isBrowser()) {
      return [];
    }
    const normalized = this.normalizeTags(tags);
    const keys = new Set<string>();
    normalized.forEach((tag) => {
      this.getKeysForTag(tag).forEach((key) => keys.add(key));
    });
    keys.forEach((key) => this.removeKey(key));
    normalized.forEach((tag) => localStorage.removeItem(this.tagPrefix + tag));
    return Array.from(keys);
  }

  removeKey(cacheKey: string): void {
    if (!this.isBrowser()) {
      return;
    }
    const tags = this.getTagsForKey(cacheKey);
    if (!tags.length) {
      return;
    }
    tags.forEach((tag) => {
      const keys = this.getKeysForTag(tag).filter((key) => key !== cacheKey);
      if (keys.length) {
        this.setKeysForTag(tag, keys);
      } else {
        localStorage.removeItem(this.tagPrefix + tag);
      }
    });
    localStorage.removeItem(this.keyPrefix + cacheKey);
  }

  clearAll(): void {
    if (!this.isBrowser()) {
      return;
    }
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(this.keyPrefix) || key.startsWith(this.tagPrefix)) {
        localStorage.removeItem(key);
      }
    });
  }

  private getTagsForKey(cacheKey: string): string[] {
    const raw = localStorage.getItem(this.keyPrefix + cacheKey);
    if (!raw) {
      return [];
    }
    try {
      const tags = JSON.parse(raw) as string[];
      return Array.isArray(tags) ? tags : [];
    } catch {
      return [];
    }
  }

  private getKeysForTag(tag: string): string[] {
    const raw = localStorage.getItem(this.tagPrefix + tag);
    if (!raw) {
      return [];
    }
    try {
      const keys = JSON.parse(raw) as string[];
      return Array.isArray(keys) ? keys : [];
    } catch {
      return [];
    }
  }

  private setKeysForTag(tag: string, keys: string[]): void {
    localStorage.setItem(this.tagPrefix + tag, JSON.stringify(keys));
  }

  private normalizeTags(tags: string[]): string[] {
    return Array.from(new Set(tags.map((tag) => tag.trim()).filter(Boolean)));
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
